import { enqueueSnackbar } from 'notistack';
import { authApi as authApiSlice } from '../features/auth/authApi';
import { persistAuth, clearAuth, normalizeUser } from '../features/auth/persistence';
import { setLegacyToken, legacyLogin } from '../features/auth/legacySync';
import { TOAST_ERROR } from '../utils/constants';
import i18next from 'i18next';
import { authApi } from '../utils/urls';

/**
 * authStatusCheck was not being called after a 401 error, so:
 * Direct call to the auth API to check session status
 * This bypasses the Redux flow and avoids circular dependency issues
 */
const directAuthStatusCheck = () => {
  const token = localStorage.getItem('token');
  return fetch(authApi(), {
    method: 'POST',
    headers: {
      token: token || '',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ method: 'status' }),
  })
    .then((res) => {
      return res.json();
    })
    .catch((err) => {
      return { error: err.message };
    });
};

const handleUnauthorized = (dispatch, url, body) => {
  // Prevent circular calls by checking if we're already in an authStatus call
  if (url === authApi() && body?.method === 'status') {
    // If we get a 401 during an authStatus call itself, then the session is truly expired
    clearAuth();
    setLegacyToken(null);
    dispatch(
      authApiSlice.util.upsertQueryData('status', undefined, {
        user: null,
        sessionId: null,
        roles: null,
        loggedIn: false,
      })
    );
    return;
  }
  // Important: Add a delay to break out of the current execution context
  // This helps avoid potential Redux dispatch issues and circular dependencies
  setTimeout(() => {
    directAuthStatusCheck()
      .then((data) => {
        if (data && data.user) {
          const { user, sessionId, roles } = data;
          // Sync legacy first, then persist modern to ensure LS 'user' stays modern
          setLegacyToken(sessionId || null);
          try {
            legacyLogin({ user, sessionId, roles, loggedIn: true, incarnated: false });
          } catch {}
          persistAuth({ sessionId, user, roles });
          // Update RTK Query auth status cache
          dispatch(
            authApiSlice.util.upsertQueryData('status', undefined, {
              user: normalizeUser(user),
              sessionId,
              roles,
              loggedIn: true,
            })
          );
        } else {
          // Session expired - clear auth (hooks will detect localStorage change)
          clearAuth();
          setLegacyToken(null);
          dispatch(
            authApiSlice.util.upsertQueryData('status', undefined, {
              user: null,
              sessionId: null,
              roles: null,
              loggedIn: false,
            })
          );
        }
      })
      .catch(() => {
        // Error checking status - assume session expired
        clearAuth();
        setLegacyToken(null);
        dispatch(
          authApiSlice.util.upsertQueryData('status', undefined, {
            user: null,
            sessionId: null,
            roles: null,
            loggedIn: false,
          })
        );
      });
  }, 0);
};

const httpClient = {
  post: (url, body, { getState, dispatch, rejectWithValue, noRedirect = false, responseType = 'json', noToken = false, signal }) => {
    const t = i18next.t;
    const authToken = localStorage.getItem('token');

    if (!url) {
      return enqueueSnackbar(t('httpClient.serverError'), { variant: TOAST_ERROR });
    }

    if (!noToken && !authToken) {
      // console.log('invalidToken');
      return;
    } else {
      const init = {
        method: 'POST',
        headers: { token: authToken },
        body: body instanceof FormData ? body : JSON.stringify(body),
        signal: signal, // Pass the signal to the fetch call
      };

      return fetch(url, init)
        .then(async (res) => {
          if (!res.ok) {
            let errorResponse;

            try {
              errorResponse = await res.json();
            } catch (e) {
              errorResponse = { errMsg: 'Failed to parse response' };
            }

            if (res.status === 500) {
              !noRedirect && enqueueSnackbar(t('httpClient.serverError'), { variant: TOAST_ERROR });
              return { data: rejectWithValue({ errorCode: res.status, response: errorResponse }) };
            } else if (res.status < 200 || res.status >= 300) {
              if (res.status === 401) {
                handleUnauthorized(dispatch, url, body);
              }

              return { data: rejectWithValue({ errorCode: res.status, response: errorResponse }) };
            } else {
              console.error(res);
              throw Error(t('httpClient.fetchError', { url }));
            }
          }

          if (responseType === 'json') {
            return res.json().then((json) => {
              if (json.reason && json.time) {
                throw Error(json.reason);
              }
              return json;
            });
          } else {
            return res;
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            // console.log(`Fetch aborted for URL: ${url}`, error);
          }
          throw error; // Re-throw error to ensure promise remains rejected
        });
    }
  },
};

export default httpClient;
